import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { AlertCircle, RefreshCw } from "lucide-react";
import { lazy, Suspense, useEffect, useState } from "react";
import { createBrowserRouter, Navigate, Outlet, RouterProvider, useLocation, useParams, useRouteError } from "react-router";
import { AppShell } from "./components/shell/AppShell";
import { ConfirmationHost, Toaster } from "./components/shell/Hosts";
import { Button, EmptyState, LogoLoader, Page, PageSpinner } from "./components/ui";
import { AuthProvider } from "./state/auth";
import { BrandScope, brandPath, lastBrand, useBrands } from "./state/brand";
import { FlowEditorRoute } from "./lib/flows";
import { isChunkLoadError, reloadForNewDeploy } from "./lib/stale-deploy";
import { FlowShortRedirect, FlowsIndexRedirect } from "./pages/flows/FlowRedirects";

const LoginPage = lazy(() => import("./pages/auth/LoginPage"));
const AcceptInvitePage = lazy(() => import("./pages/auth/AcceptInvitePage"));
const HomePage = lazy(() => import("./pages/home/HomePage"));
const AllBrandsPage = lazy(() => import("./pages/home/AllBrandsPage"));
const DecisionsPage = lazy(() => import("./pages/decisions/DecisionsPage"));
const DailyCheckPage = lazy(() => import("./pages/check/DailyCheckPage"));
const AdMonitorPage = lazy(() => import("./pages/monitor/AdMonitorPage"));
const CreativePerformancePage = lazy(() => import("./pages/performance/CreativePerformancePage"));
const CreationsPage = lazy(() => import("./pages/creations/CreationsPage"));
const StudioPage = lazy(() => import("./pages/studio/StudioPage"));
const BrandAssetsPage = lazy(() => import("./pages/assets/BrandAssetsPage"));
const InspirationPage = lazy(() => import("./pages/inspiration/InspirationPage"));
const BrandsPage = lazy(() => import("./pages/brands/BrandsPage"));
const OnboardBrandPage = lazy(() => import("./pages/brands/OnboardBrandPage"));
const BrandSettingsPage = lazy(() => import("./pages/brands/BrandSettingsPage"));
const ConnectionsPage = lazy(() => import("./pages/connections/ConnectionsPage"));
const PartnershipsPage = lazy(() => import("./pages/partnerships/PartnershipsPage"));
const SchedulerPage = lazy(() => import("./pages/scheduler/SchedulerPage"));
const SettingsPage = lazy(() => import("./pages/settings/SettingsPage"));
const ChatHomePage = lazy(() => import("./pages/chat/ChatHomePage"));
const ChatThreadPage = lazy(() => import("./pages/chat/ChatThreadPage"));
const ArtifactPage = lazy(() => import("./pages/chat/ArtifactPage"));
const FlowsPage = lazy(() => import("./pages/flows/FlowsPage"));
const CreativeEditorPage = lazy(() => import("./pages/editor/CreativeEditorPage"));

/** "/" → last brand's Home, else first brand, else onboarding (or All brands when brands can't load). */
function RootRedirect() {
  const { brands, isLoading, error } = useBrands();
  if (isLoading) return <PageSpinner />;
  const last = lastBrand();
  if (last && brands.some((b) => b.id === last)) return <Navigate to={brandPath(last, "home")} replace />;
  if (brands[0]) return <Navigate to={brandPath(brands[0].id, "home")} replace />;
  if (error) return <Navigate to="/all" replace />;
  return <Navigate to="/brands/new" replace />;
}

/**
 * Server-side redirects (OAuth callbacks) land on /brands/:brandId/connections?connected=… and
 * /connections?oauth_error=…&provider=… — forward them to the brand-scoped page, keeping the query.
 */
function ConnectionsRedirect() {
  const { brandId } = useParams();
  const { search } = useLocation();
  const target = brandId ?? lastBrand();
  return <Navigate to={target ? `${brandPath(target, "connections")}${search}` : `/brands${search}`} replace />;
}

/**
 * App links produced by the server (Tasks panel `resultRefUrl`, notifications) use a `/brands/:id/…` scheme.
 * Map each onto the web route that shows the object, keeping query strings.
 */
function ServerLinkRedirect({ to }: { to: "home" | "reference" | "creatives" | "creative" | "artifact" | "ideas" | "hooks" | "alerts" }) {
  const { brandId = "", refId, creativeId, artifactId } = useParams();
  const { search } = useLocation();
  const q = new URLSearchParams(search);
  const b = (page: string) => brandPath(brandId, page);
  let target: string;
  switch (to) {
    case "reference":
      target = `${b("inspiration")}?ref=${encodeURIComponent(refId ?? "")}`;
      break;
    case "creatives":
      target = q.get("batch") ? `${b("studio")}?batch=${encodeURIComponent(q.get("batch")!)}` : b("creations");
      break;
    case "creative":
      target = `${b("creations")}?creative=${encodeURIComponent(creativeId ?? "")}`;
      break;
    case "artifact":
      target = `/reports/${encodeURIComponent(artifactId ?? "")}`;
      break;
    case "ideas":
      target = `${b("inspiration")}?tab=ideas`;
      break;
    case "hooks":
      target = `${b("settings")}?section=hooks`;
      break;
    case "alerts":
      target = "/settings/alerts";
      break;
    default:
      target = b("home");
  }
  return <Navigate to={target} replace />;
}

function NotFound() {
  return (
    <Page>
      <EmptyState title="Page not found" body="That link doesn't go anywhere. Use ⌘K to jump to a page." />
    </Page>
  );
}

/** Route-level crash screen (replaces React Router's developer default). A stale chunk reloads once instead. */
export function RouteError() {
  const error = useRouteError();
  const stale = isChunkLoadError(error);
  const [blocked, setBlocked] = useState(false);
  useEffect(() => {
    if (stale && !reloadForNewDeploy()) setBlocked(true);
  }, [stale]);
  if (stale && !blocked) {
    return (
      <div className="grid h-full place-items-center">
        <LogoLoader label="Loading the latest version…" />
      </div>
    );
  }
  return (
    <div className="grid h-full place-items-center">
      <EmptyState
        icon={<AlertCircle className="size-5" strokeWidth={1.75} />}
        title={stale ? "A new version is available" : "Something went wrong"}
        body={stale ? "Reload to finish updating Mimic Ads Studio." : "This page hit an unexpected error. Reloading usually fixes it."}
        action={
          <Button variant="primary" size="md" icon={<RefreshCw className="size-4" strokeWidth={1.75} />} onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      />
    </div>
  );
}

function Root() {
  return (
    <AuthProvider>
      <Suspense
        fallback={
          <div className="grid h-full place-items-center">
            <LogoLoader />
          </div>
        }
      >
        <Outlet />
      </Suspense>
      <ConfirmationHost />
      <Toaster />
    </AuthProvider>
  );
}

export const routes = [
  {
    element: <Root />,
    errorElement: <RouteError />,
    children: [
      { path: "/login", element: <LoginPage /> },
      { path: "/invite/:token", element: <AcceptInvitePage /> },
      // Full-screen flow editor (features/flows, mounted optionally — see lib/flows.tsx).
      { path: "/b/:brandId/flows/:flowId", element: <FlowEditorRoute /> },
      { path: "/flows/:flowId", element: <FlowShortRedirect /> },
      { path: "/flows", element: <FlowsIndexRedirect /> },
      // Full-screen creative editor (PlayAd "Open in editor") — no rail, like the flow editor.
      { path: "/b/:brandId/editor/:creativeId", element: <CreativeEditorPage /> },
      {
        element: <AppShell />,
        children: [
          { path: "/", element: <RootRedirect /> },
          { path: "/all", element: <AllBrandsPage /> },
          { path: "/decisions", element: <DecisionsPage /> },
          { path: "/brands", element: <BrandsPage /> },
          { path: "/brands/new", element: <OnboardBrandPage /> },
          { path: "/brands/:brandId/onboarding", element: <OnboardBrandPage /> },
          { path: "/brands/:brandId/connections", element: <ConnectionsRedirect /> },
          { path: "/brands/:brandId", element: <ServerLinkRedirect to="home" /> },
          { path: "/brands/:brandId/inspiration/references/:refId", element: <ServerLinkRedirect to="reference" /> },
          { path: "/brands/:brandId/creatives", element: <ServerLinkRedirect to="creatives" /> },
          { path: "/brands/:brandId/creatives/:creativeId", element: <ServerLinkRedirect to="creative" /> },
          { path: "/brands/:brandId/artifacts/:artifactId", element: <ServerLinkRedirect to="artifact" /> },
          { path: "/brands/:brandId/ideas", element: <ServerLinkRedirect to="ideas" /> },
          { path: "/brands/:brandId/hooks", element: <ServerLinkRedirect to="hooks" /> },
          { path: "/brands/:brandId/alerts", element: <ServerLinkRedirect to="alerts" /> },
          { path: "/connections", element: <ConnectionsRedirect /> },
          { path: "/chat", element: <ChatHomePage /> },
          { path: "/chat/:threadId", element: <ChatThreadPage /> },
          { path: "/reports/:artifactId", element: <ArtifactPage /> },
          { path: "/settings", element: <Navigate to="/settings/team" replace /> },
          { path: "/settings/:section", element: <SettingsPage /> },
          {
            path: "/b/:brandId",
            element: <BrandScope />,
            children: [
              { index: true, element: <Navigate to="home" replace /> },
              { path: "home", element: <HomePage /> },
              { path: "decisions", element: <DecisionsPage /> },
              { path: "check", element: <DailyCheckPage /> },
              { path: "monitor", element: <AdMonitorPage /> },
              { path: "monitor/:dashboardId", element: <AdMonitorPage /> },
              { path: "performance", element: <CreativePerformancePage /> },
              { path: "creations", element: <CreationsPage /> },
              { path: "studio", element: <StudioPage /> },
              { path: "flows", element: <FlowsPage /> },
              { path: "assets", element: <BrandAssetsPage /> },
              { path: "inspiration", element: <InspirationPage /> },
              { path: "connections", element: <ConnectionsPage /> },
              { path: "partnerships", element: <PartnershipsPage /> },
              { path: "scheduler", element: <SchedulerPage /> },
              { path: "settings", element: <BrandSettingsPage /> },
            ],
          },
          { path: "*", element: <NotFound /> },
        ],
      },
    ],
  },
];

export function App() {
  const [queryClient] = useState(
    () =>
      new QueryClient({
        defaultOptions: {
          queries: { refetchOnWindowFocus: false, staleTime: 15_000 },
          mutations: { retry: false },
        },
      }),
  );
  const [router] = useState(() => createBrowserRouter(routes));
  return (
    <QueryClientProvider client={queryClient}>
      <RouterProvider router={router} />
    </QueryClientProvider>
  );
}
